import type { LicenceSnapshot } from '../../api/types';
import type { Message } from '../../i18n/translate';

/**
 * MVP-513, the web half. The venue-facing countdown: "your licence runs out in N days", shown every
 * day from seven days out, through the expiry date itself, and through the seven-day grace window
 * after it — so nobody is ever cut off without a fortnight of being told, every single day of it.
 *
 * Pure and DOM-free (same shape as `storefrontMode.ts`/`playbackMath.ts`): every decision here is a
 * function of (`licence`, an instant) and nothing else, so the day boundary this whole feature turns
 * on is testable without a clock and without a browser. The React wiring is `useLicenceCountdown.ts`
 * and the strip itself is `LicenceExpiryBar.tsx`.
 *
 * === THE MIDNIGHT PROBLEM, AND WHICH WAY IT WAS ANSWERED ===
 * `GET /api/session/whoami` hands back a server-computed `countdown` ONCE, on load. The room PC is
 * open all night, and midnight is precisely when that number changes. Three options were on the
 * table; this module is the third.
 *
 *   (a) Recompute in the browser from `expiresAt` alone. Rejected as written: it duplicates the
 *       rule, and — the failure that actually matters — a room PC with a wrong clock would count
 *       down to a different day than the server refuses on. That is the worst possible version of
 *       this feature: a shop told "3 days left" on the morning the backend already says no.
 *   (b) Re-fetch `whoami` on a timer. Rejected: it puts a repeating authenticated request on a shell
 *       that otherwise makes exactly one, purely so a number can change once a day; and its OWN
 *       failure mode is silent — a poll that starts 401ing (expired JWT, flaky venue wifi) leaves
 *       the banner frozen on yesterday's number with nothing on screen to say the count has stopped,
 *       which is the same bug as computing once at mount, only harder to see.
 *   (c) ANCHOR, then tick locally — what `anchorLicence` + `licenceBannerView` below do. The
 *       server's `daysRemaining` is taken as the truth AT THE MOMENT THE PAYLOAD ARRIVES, and the
 *       difference between it and the browser's own reading of the same instant is kept as
 *       `skewDays` and added to every later recomputation. So the count is always the server's
 *       count, moved forward by whole days the BROWSER observed passing: a machine whose clock is a
 *       day out is corrected on arrival (which (a) never was), and no repeating request is issued
 *       (which (b) needed). The one thing it cannot see is the licence being renewed mid-night in
 *       Strapi; that lands on the next reload, which is the same latency every other whoami-derived
 *       fact on this shell already has.
 *
 * The day arithmetic below is a deliberate, line-by-line mirror of
 * `strapi-template/src/utils/licence-expiry.js` — its own header explains why Asia/Bangkok is a
 * hardcoded +07:00 constant rather than an `Intl` timeZone lookup, and that reasoning applies twice
 * over in a browser, where the ICU data present is the visitor's business and not ours. The mirror
 * is not trusted to stay a mirror: `licenceCountdown.test.ts` `require()`s that very file and
 * compares the two implementations day by day across the whole window.
 */

/** The last N days before `expiresAt` are the warning window — the countdown runs 7 down to 0. */
export const COUNTDOWN_DAYS = 7;

/** …and the N days after it are the grace window, in which nothing changes for anyone. */
export const GRACE_DAYS = 7;

/** Named here for the same reason the backend names it: the venues are standing in this zone. */
export const LICENCE_TIMEZONE = 'Asia/Bangkok';

/** Thailand has never observed daylight saving, so the zone reduces to one number. */
const BANGKOK_UTC_OFFSET_MINUTES = 7 * 60;

const MS_PER_DAY = 86_400_000;
const DATE_ONLY_RE = /^(\d{4})-(\d{2})-(\d{2})/;

/**
 * Which Bangkok civil day an instant falls on, as days since 1970-01-01 there. Only ever subtracted
 * from another day number, so the epoch is arbitrary — what matters is that both ends of every
 * subtraction are counted the same way.
 */
export function bangkokDayNumber(nowMs: number): number | null {
  if (!Number.isFinite(nowMs)) return null;
  return Math.floor((nowMs + BANGKOK_UTC_OFFSET_MINUTES * 60_000) / MS_PER_DAY);
}

/**
 * The same day number for the stored `expiresAt`, read by its YYYY-MM-DD head and NOT put through a
 * timezone conversion: it is a civil date off a contract, and re-interpreting "2026-10-01" as an
 * instant is how a date column loses a day.
 *
 * Anything unparseable answers null, which every caller turns into "no banner". Failing SILENT is
 * the only defensible direction here: a value this cannot read must never become a countdown to a
 * day nobody agreed on, and the backend — which fails OPEN on the same input, still serving — is
 * where the consequence of an unreadable date is decided.
 */
export function expiryDayNumber(expiresAt: string | null | undefined): number | null {
  if (typeof expiresAt !== 'string') return null;
  const match = expiresAt.trim().match(DATE_ONLY_RE);
  if (!match) return null;
  const ms = Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  if (!Number.isFinite(ms)) return null;
  return Math.floor(ms / MS_PER_DAY);
}

/** A Bangkok day number back as its civil date, `YYYY-MM-DD` — the per-day dismissal's key. */
export function bangkokDayKey(dayNumber: number): string {
  return new Date(dayNumber * MS_PER_DAY).toISOString().slice(0, 10);
}

/**
 * What the browser keeps between whoami calls: the expiry day, plus the whole-day disagreement
 * between this machine's clock and the server's at the instant the payload landed. See option (c) in
 * the header — `skewDays` is what makes the local tick the SERVER's count rather than a second
 * opinion about it.
 */
export interface LicenceAnchor {
  /** Bangkok day number of `expiresAt`. */
  expiryDay: number;
  /** Server days minus locally-derived days, at receipt. Zero on a machine with the right date. */
  skewDays: number;
  /** Carried through so the strip can name the customer the licence belongs to. */
  customerName: string | null;
}

/**
 * Anchor a freshly-arrived payload against this browser's clock.
 *
 * Null for every case with nothing to count: no licence on the account, no readable date, or an
 * `unlimited` licence. `served: false` (more than a week past expiry) is deliberately NOT excluded
 * here — `licenceBannerView` decides what to show, and the reason a lapsed licence shows nothing is
 * written there rather than being hidden in a guard clause.
 */
export function anchorLicence(licence: LicenceSnapshot | null | undefined, receivedAtMs: number): LicenceAnchor | null {
  if (!licence) return null;
  const expiryDay = expiryDayNumber(licence.expiresAt);
  const receivedDay = bangkokDayNumber(receivedAtMs);
  if (expiryDay == null || receivedDay == null) return null;
  // A payload from an older backend that does not send `daysRemaining` anchors at zero skew, which
  // is exactly case (a) — still correct on every machine whose date is right, and the honest
  // fallback when there is no server answer to anchor against.
  const skewDays = typeof licence.daysRemaining === 'number' ? licence.daysRemaining - (expiryDay - receivedDay) : 0;
  return { expiryDay, skewDays, customerName: licence.customerName ?? null };
}

/** The strip's contents, or null when there is nothing to say today. */
export interface LicenceBannerView {
  /** Signed Bangkok days to expiry, the server's count moved forward locally: 7, 0, -1, -7. */
  daysRemaining: number;
  /** Before or on the date, vs. inside the grace window — two different sentences, one strip. */
  tone: 'ending' | 'grace';
  /** The sentence itself, as a key + params: this module is tested under node, with no provider. */
  message: Message;
  /** The Bangkok civil date this strip is FOR. The dismissal is keyed by it, so it expires itself. */
  dayKey: string;
  customerName: string | null;
}

/**
 * The whole "show it or not, and what does it say" decision, for one instant.
 *
 * Shown for the countdown window and the whole grace window, and NOT once the licence has lapsed.
 * That last one is a real choice: past the grace window the backend refuses the operator's requests
 * outright with its own sentence (`LICENCE_LAPSED_MESSAGE`), so a strip repeating it would be the
 * second-loudest thing on a screen that has already stopped working — and the admin's side of a
 * lapsed customer is the Licences screen, which is its own ticket.
 */
export function licenceBannerView(anchor: LicenceAnchor | null, nowMs: number): LicenceBannerView | null {
  if (!anchor) return null;
  const today = bangkokDayNumber(nowMs);
  if (today == null) return null;

  const daysRemaining = anchor.expiryDay - today + anchor.skewDays;
  if (daysRemaining > COUNTDOWN_DAYS) return null; // not yet in the window: say nothing
  if (daysRemaining < -GRACE_DAYS) return null; // lapsed — see the doc comment above

  // Dismissal is keyed by the day the venue is actually living in — derived BACK from the count
  // rather than from this machine's own date, so a browser three days fast dismisses (and un-
  // dismisses) on the same day the server would. Written as expiry-minus-count for that reason: the
  // equivalent `today - skewDays` is the same number and says nothing about why.
  const dayKey = bangkokDayKey(anchor.expiryDay - daysRemaining);
  const tone = daysRemaining < 0 ? 'grace' : 'ending';
  return { daysRemaining, tone, dayKey, customerName: anchor.customerName, message: bannerMessage(daysRemaining) };
}

/**
 * Four sentences rather than one with a number in it. "…in 1 days" is the least of it: the day of
 * expiry and the last served day are both moments where a count is the wrong thing to print, and a
 * venue reading "0" has to work out what 0 means while the KJ is waiting.
 */
function bannerMessage(daysRemaining: number): Message {
  if (daysRemaining > 0) return { key: 'ops.licence.ending', params: { days: daysRemaining } };
  if (daysRemaining === 0) return { key: 'ops.licence.endingToday' };
  const graceDaysLeft = GRACE_DAYS + daysRemaining; // 0 on the last day service is still given
  if (graceDaysLeft === 0) return { key: 'ops.licence.graceLastDay', params: { days: -daysRemaining } };
  return { key: 'ops.licence.grace', params: { days: -daysRemaining, graceDays: graceDaysLeft } };
}

/**
 * How often the watcher below looks at the clock. Thirty seconds, not "schedule a timeout for
 * midnight": a `setTimeout` aimed hours ahead is exactly what a suspended room PC (screen blanked,
 * laptop lid shut, browser throttling a background tab) fires late or coalesces, and one missed fire
 * is the whole night's countdown gone. Polling the clock cannot miss a day — it can only notice it
 * up to thirty seconds late, and thirty seconds is nothing to a number that changes once a day.
 */
export const DAY_WATCH_INTERVAL_MS = 30_000;

/**
 * Call `onDayChange` whenever the Bangkok civil day turns over, and never otherwise. Returns its own
 * teardown, so a React effect can `return createDayWatcher(...)` directly.
 *
 * The comparison is on the day NUMBER rather than on the tick, so a night of ticking costs 2,880
 * cheap integer comparisons and exactly one re-render. `now`/`schedule` are injected so
 * `licenceCountdown.test.ts` can cross a real midnight without waiting for one.
 */
export function createDayWatcher(
  onDayChange: (nowMs: number) => void,
  options: { now?: () => number; intervalMs?: number } = {}
): () => void {
  const now = options.now ?? (() => Date.now());
  const intervalMs = options.intervalMs ?? DAY_WATCH_INTERVAL_MS;
  let lastDay = bangkokDayNumber(now());
  const timer = setInterval(() => {
    const nowMs = now();
    const day = bangkokDayNumber(nowMs);
    if (day === lastDay) return;
    lastDay = day;
    onDayChange(nowMs);
  }, intervalMs);
  return () => clearInterval(timer);
}

/**
 * The per-day dismissal, per machine.
 *
 * `localStorage` and never Strapi, for `roomSelection.ts`/`storefrontMode.ts`'s reason: which screen
 * someone is standing at is a fact about the machine, and the KJ's phone should not inherit a
 * dismissal from the room PC. Keyed by the Bangkok civil date so it expires on its own — "dismissed
 * forever" defeats the owner's "every day", and a dismissal that needed clearing is a second thing
 * to get wrong.
 */
export const LICENCE_DISMISS_STORAGE_KEY = 'mvcp.licence.dismissedDay.v1';

/** Pure. Anything that is not a plain `YYYY-MM-DD` string reads as "not dismissed" — a corrupt or
 * hand-edited blob must fail towards SHOWING the warning, never towards hiding it. */
export function parseDismissedDay(raw: unknown): string | null {
  return typeof raw === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(raw) ? raw : null;
}

function hasLocalStorage(): boolean {
  return typeof localStorage !== 'undefined';
}

export function loadDismissedDay(): string | null {
  if (!hasLocalStorage()) return null;
  try {
    return parseDismissedDay(localStorage.getItem(LICENCE_DISMISS_STORAGE_KEY));
  } catch {
    return null; // blocked/private-mode storage shows the warning, which is the safe direction
  }
}

export function saveDismissedDay(dayKey: string): void {
  if (!hasLocalStorage()) return;
  try {
    localStorage.setItem(LICENCE_DISMISS_STORAGE_KEY, dayKey);
  } catch {
    /* storage full or blocked — the strip simply stays up, which is not a failure worth throwing on */
  }
}
